import {
  generateBindings,
  observableValue,
  useSharedState,
  view,
  WritableObservableValue,
} from '@esui/core';
import { spread } from '@esui/core/utils/spread.js';
import '@material/mwc-button';
import '@material/mwc-icon-button';
import '@material/mwc-snackbar';
import { html } from 'lit';

type Reason = 'action' | 'dismiss';

class Props {
  open = observableValue(false);
  // value between 4000 and 10000 or -1 to disable timeout
  timeoutMs = 5000;
  closeOnEscape = false;
  labelText = '';
  stacked = false;
  leading = false;
  actionButtonLabel = '';
  dismissButtonIcon = '';
  onOpening?: () => void = undefined;
  onOpened?: () => void = undefined;
  onClosing?: (reason?: Reason) => void = undefined;
  onClosed?: (reason?: Reason) => void = undefined;
}

export const [Snackbar] = view('esui-mwc-snackbar', {
  Props,

  mapBuilder:
    SnackbarBuilder =>
    (open: WritableObservableValue<boolean>, labelText: string) =>
      SnackbarBuilder().open(open).labelText(labelText),

  template(props) {
    const open = useSharedState(props.open);
    const computedProps = generateBindings(props, [
      'open',
      'onOpening',
      'onOpened',
      'onClosing',
      'onClosed',
      'actionButtonLabel',
      'dismissButtonIcon',
      'timeoutMs',
    ]);

    function handleClosed(e: { detail: { reason?: Reason } }) {
      props.onClosed?.(e.detail.reason);
      open.value = false;
    }

    function action() {
      if (props.actionButtonLabel.trim()) {
        return html` <mwc-button slot="action"
          >${props.actionButtonLabel}
        </mwc-button>`;
      }
      return html``;
    }

    function dismiss() {
      if (props.dismissButtonIcon) {
        return html` <mwc-icon-button
          icon="${props.dismissButtonIcon}"
          slot="dismiss"
        ></mwc-icon-button>`;
      }
      return html``;
    }

    return html`
      <mwc-snackbar
        ${spread(computedProps)}
        ?open="${open.value}"
        .timeoutMs="${processTimeout(props.timeoutMs)}"
        @MDCSnackbar:opening="${() => props.onOpening?.()}"
        @MDCSnackbar:opened="${() => props.onOpened?.()}"
        @MDCSnackbar:closing="${(e: any) => props.onClosing?.(e.detail.reason)}"
        @MDCSnackbar:closed="${handleClosed}"
      >
        ${action()} ${dismiss()}
      </mwc-snackbar>
    `;
  },
});

function processTimeout(t: number) {
  if (t === -1 || (t >= 4000 && t <= 10000)) {
    return t;
  }
  return 5000;
}
